import type { Participant } from './callRoom';
import { ParticipantTile } from './Tile';
import { CallChat } from './CallChat';
import { Mosaic, useAspects } from './Mosaic';
import type { MosaicConfig } from './mosaicMath';
import { IconCollapse } from '../../components/icons';

/** Настройки мозаики для узкой панели: зазор мельче, чем на сцене, и в строке
 *  не больше двух плиток — иначе лица становятся нечитаемыми. */
const SIDE_MOSAIC: Partial<MosaicConfig> = { gap: 4, maxPerRow: 2, minTileW: 60, minTileH: 48 };

/*
 * Правая панель сцены звонка: одна на всё, с двумя вкладками — «Лица» и
 * «Переписка» (решение Ильи 11.09.2026). Второй выезжающей панели с того же
 * края не заводим: у сцены уже есть панель рисования слева, и третья штука,
 * приезжающая справа, путает.
 */
export function CallSide({
  people,
  tab,
  onTab,
  unread,
  onCollapse,
  onPick,
  leaving,
}: {
  people: Participant[];
  /** Панель уезжает: показывается ещё мгновение ради анимации ухода. */
  leaving?: boolean;
  tab: 'faces' | 'chat';
  onTab: (tab: 'faces' | 'chat') => void;
  /** Непрочитанные сообщения — числом на вкладке, пока она закрыта. */
  unread: number;
  onCollapse: () => void;
  onPick?: (p: Participant, anchor: HTMLElement) => void;
}) {
  const { aspects, report } = useAspects();
  return (
    /* Обёртка без обрезки: сама панель режет содержимое по скруглению, а
       кнопка сворачивания висит снаружи, слева от её края (решение Ильи
       15.09.2026). Внутри панели она была бы поверх вкладок. */
    <div className={`call-side-wrap${leaving ? ' out' : ''}`}>
      <button
        type="button"
        className="sb-collapse call-side-collapse"
        onClick={onCollapse}
        title="Свернуть панель"
        data-tip="Свернуть панель"
      >
        <IconCollapse />
      </button>
      <div className="call-side">
      <div className="seg call-side-tabs">
        <button type="button" className={tab === 'faces' ? 'on' : ''} onClick={() => onTab('faces')}>
          Лица
        </button>
        <button type="button" className={tab === 'chat' ? 'on' : ''} onClick={() => onTab('chat')}>
          Переписка
          {tab !== 'chat' && unread > 0 && <i className="counter">{unread}</i>}
        </button>
      </div>
      {tab === 'faces' ? (
        /* Миниатюры кладёт та же мозаика, что и сцену (решение Ильи
           15.09.2026): плитка принимает форму своего кадра, вместо того чтобы
           вписываться в клетку фиксированной высоты. Поля вокруг минимальные —
           места в панели мало. */
        <div className="call-side-faces">
          <Mosaic
            items={people.map((p) => ({ id: p.id, aspect: aspects[p.id] }))}
            config={SIDE_MOSAIC}
            render={(id) => {
              const p = people.find((x) => x.id === id);
              if (!p) return null;
              return (
                <ParticipantTile
                  p={p}
                  size="sm"
                  onPick={onPick ? (anchor) => onPick(p, anchor) : undefined}
                  onAspect={(a) => report(p.id, a)}
                />
              );
            }}
          />
        </div>
      ) : (
        <CallChat />
      )}
      </div>
    </div>
  );
}
